import React from 'react';
import { Check, ShieldAlert } from 'lucide-react';
import { API_SCOPES } from '@atlas/shared';
import { Input } from '@/components/ui/input';
import { toDateInputValue, addNzDays } from '@/lib/format-date';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * The fields shared by issuing a key and editing one.
 *
 * Extracted so the two pages cannot drift: a scope described one way on
 * creation and another way on edit is how someone ends up granting more than
 * they meant to.
 */

export interface KeyDraft {
  name: string;
  scopes: string[];
  expiresAt: string;   // yyyy-mm-dd, '' = never
  rateLimit: string;   // '' = system default
}

export const EXPIRY_PRESETS: Array<{ label: string; days: number | null }> = [
  { label: 'No expiry', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

/** Common shapes, so the usual case is one click and the reasoning is named. */
export const SCOPE_PRESETS: Array<{ label: string; description: string; scopes: string[] }> = [
  {
    label: 'Dispatch integration',
    description: 'Create, update and delete dispatch jobs, and look up item codes. What a weighbridge or ordering system needs.',
    // Deliberately WITHOUT `dispatch:list`. A partner holds its own
    // externalRefs and reads jobs back one at a time; paging the whole
    // collection is a separate decision, made by ticking the box.
    scopes: ['dispatch:read', 'dispatch:create', 'dispatch:update', 'dispatch:delete', 'catalogue:read'],
  },
  {
    label: 'Read only',
    description: 'Look, don’t touch. Safe for reporting, dashboards and monitoring.',
    scopes: API_SCOPES.filter((s) => !s.write).map((s) => s.value),
  },
];

export function isoFromDays(days: number | null): string {
  if (days === null) return '';
  // NZ calendar day — toISOString() would show the preset expiring a day
  // early through an NZ morning.
  return toDateInputValue(addNzDays(new Date(), days));
}

export function NameCard({ draft, onChange }: { draft: KeyDraft; onChange: (d: KeyDraft) => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Name</CardTitle>
        <CardDescription>
          Usually the partner or tool. It appears in the audit trail against every change this key makes,
          so make it recognisable a year from now.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Input
          value={draft.name}
          onChange={(e) => onChange({ ...draft, name: e.target.value })}
          placeholder="Weighbridge integration"
          className="max-w-xl"
        />
      </CardContent>
    </Card>
  );
}

export function AccessCard({
  draft, onChange, changeNote,
}: {
  draft: KeyDraft;
  onChange: (d: KeyDraft) => void;
  /** Extra line shown when editing, e.g. what is being added or removed. */
  changeNote?: React.ReactNode;
}) {
  const groups = React.useMemo(() => {
    const m = new Map<string, typeof API_SCOPES[number][]>();
    for (const s of API_SCOPES) m.set(s.group, [...(m.get(s.group) ?? []), s]);
    return [...m.entries()];
  }, []);

  const toggle = (value: string) => onChange({
    ...draft,
    scopes: draft.scopes.includes(value)
      ? draft.scopes.filter((s) => s !== value)
      : [...draft.scopes, value],
  });

  const writeSelected = API_SCOPES.filter((s) => s.write && draft.scopes.includes(s.value));

  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <CardTitle className="text-base">
          Access <span className="text-muted-foreground font-normal">— {draft.scopes.length} selected</span>
        </CardTitle>
        <CardDescription>Grant the least that lets the integration do its job.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          {SCOPE_PRESETS.map((p) => (
            <Button
              key={p.label}
              type="button"
              size="sm"
              variant="outline"
              title={p.description}
              onClick={() => onChange({ ...draft, scopes: p.scopes })}
            >
              {p.label}
            </Button>
          ))}
          {draft.scopes.length > 0 && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange({ ...draft, scopes: [] })}>
              Clear
            </Button>
          )}
        </div>

        {groups.map(([group, list]) => (
          <div key={group}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">{group}</div>
            <div className="grid gap-1.5 2xl:grid-cols-2">
              {list.map((s) => {
                const on = draft.scopes.includes(s.value);
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => toggle(s.value)}
                    aria-pressed={on}
                    className={`w-full text-left border rounded-md px-3 py-2.5 transition-colors ${
                      on ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <span
                        aria-hidden
                        className={`mt-0.5 h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${
                          on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40'
                        }`}
                      >
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs">{s.value}</span>
                          {s.write
                            ? <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Write</Badge>
                            : <Badge variant="outline" className="text-[10px] px-1.5 py-0">Read</Badge>}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{s.description}</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {changeNote}

        {writeSelected.length > 0 && (
          <div className="flex items-start gap-2 text-xs border rounded-md px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-900">
            <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
            <span>
              This key can <strong>change your data</strong>
              {writeSelected.some((s) => s.value.endsWith(':delete')) && ' — including permanent deletion'}.
              Anyone holding it can act without a login.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function LimitsCard({ draft, onChange }: { draft: KeyDraft; onChange: (d: KeyDraft) => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Limits</CardTitle>
        <CardDescription>Both optional. Sensible defaults if you skip them.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs">Expires</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {EXPIRY_PRESETS.map((p) => {
              const value = isoFromDays(p.days);
              return (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  variant={draft.expiresAt === value ? 'default' : 'outline'}
                  onClick={() => onChange({ ...draft, expiresAt: value })}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          <Input
            type="date"
            className="mt-1.5 max-w-xs"
            value={draft.expiresAt}
            onChange={(e) => onChange({ ...draft, expiresAt: e.target.value })}
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Rotation means issuing a new key and revoking this one — agree the date with the partner first.
          </p>
        </div>

        <div>
          <Label className="text-xs">Rate limit</Label>
          <Input
            type="number"
            min={1}
            className="max-w-xs"
            value={draft.rateLimit}
            onChange={(e) => onChange({ ...draft, rateLimit: e.target.value })}
            placeholder="Requests per minute"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Caps this integration without affecting any other. Blank uses the system default.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

/** Body for POST/PATCH from a draft. Shared so both pages send the same shape. */
export function draftToBody(draft: KeyDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    scopes: draft.scopes,
    expiresAt: draft.expiresAt ? new Date(`${draft.expiresAt}T23:59:59`).toISOString() : null,
    rateLimitPerMinute: draft.rateLimit.trim() ? Number(draft.rateLimit) : null,
  };
}
